import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { cellsForColumn, columnDisplayName } from '../../utils/plottrackGrid';
import {
  ADD_SCENE,
  ADD_SCENE_PART,
  ADD_SCENE_ADDED,
  ADD_SCENE_AFTER,
  ADD_SCENE_CANCEL,
  ADD_SCENE_NEEDS_WHAT,
  ADD_SCENE_SAVE,
  ADD_SCENE_SAVING,
  ADD_SCENE_SUGGEST,
  ADD_SCENE_SUGGESTING,
  ADD_SCENE_SUGGEST_NEEDS_WHAT,
  ADD_SCENE_TITLE,
  ADD_SCENE_TITLE_HINT,
  ADD_SCENE_WHAT,
  ADD_SCENE_WHAT_HINT,
} from '../../utils/writerCopy';
import { clearPlotTrackDraft, readPlotTrackDraft, writePlotTrackDraft } from './plotTrackDraft';

/**
 * Run 36: Generate stopped short of the writer's ending, and with no
 * framework there was no way to add a scene. This adds one after any scene.
 * What happens is required; the title is optional (blank = the first words
 * of what happens). Suggest title is one small paid call, confirmed first.
 * Run 46: the choices are grouped by sequence, since the new scene joins
 * the sequence of the scene it goes after.
 */
const sceneOption = ({ scene, number }) => (
  <option key={scene.id} value={scene.id}>{`${number}. ${scene.name}`}</option>
);

/** Runs of consecutive scenes by sequence name; unnamed runs get no group. */
export function afterSceneGroups(scenes, sequences) {
  const sequenceOf = new Map();
  for (const header of sequences || []) {
    const name = String(header?.display_name || '').trim();
    if (!name) continue;
    for (const id of header.scene_ids || []) sequenceOf.set(String(id), name);
  }
  const groups = [];
  (scenes || []).forEach((scene, index) => {
    const name = sequenceOf.get(scene.id) || '';
    const last = groups[groups.length - 1];
    const row = { scene, number: index + 1 };
    if (last && last.name === name) last.scenes.push(row);
    else groups.push({ key: `${index}-${name}`, name, scenes: [row] });
  });
  return groups;
}

export default function PlotTrackAddScene({
  columns, cells, sequences, defaultAfterSceneId, onAdd, onSuggestTitle,
  idPrefix = 'pt-add-scene',
  buttonLabel = ADD_SCENE,
  partName = '',
  draftKey = '',
}) {
  const saved = readPlotTrackDraft(draftKey);
  const scenes = useMemo(() => (columns || [])
    .filter((column) => column?.scene_id)
    .map((column) => ({
      id: String(column.scene_id),
      name: columnDisplayName(column, cellsForColumn(column, cells || [])) || 'Untitled scene',
    })), [columns, cells]);
  const groups = useMemo(() => afterSceneGroups(scenes, sequences), [scenes, sequences]);
  const fallbackAfter = defaultAfterSceneId
    ? String(defaultAfterSceneId)
    : scenes[scenes.length - 1]?.id || '';
  const [open, setOpen] = useState(Boolean(saved?.open));
  const [after, setAfter] = useState(saved?.after || '');
  const [what, setWhat] = useState(saved?.what || '');
  const [title, setTitle] = useState(saved?.title || '');
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState(null);
  const [added, setAdded] = useState(null);
  const titleRef = useRef(null);
  const restoreTitleFocus = useRef(false);
  const titleCaret = useRef(null);
  const { ask, dialog } = usePaidConfirm();

  useEffect(() => {
    if (!draftKey) return undefined;
    if (!open) {
      clearPlotTrackDraft(draftKey);
      return undefined;
    }
    writePlotTrackDraft(draftKey, { open: true, after, what, title });
    return undefined;
  }, [draftKey, open, after, what, title]);

  // #2046: a refresh of the scene list must not drop the title caret.
  const restoreTitleCaret = () => {
    if (!restoreTitleFocus.current) return;
    const node = titleRef.current;
    if (!node) return;
    if (document.activeElement !== node) node.focus();
    const caret = titleCaret.current;
    if (document.activeElement === node && caret != null) {
      node.setSelectionRange(caret, caret);
    }
  };

  useLayoutEffect(() => {
    restoreTitleCaret();
  });

  useEffect(() => {
    restoreTitleCaret();
    restoreTitleFocus.current = false;
  });

  const begin = () => {
    setAfter(fallbackAfter);
    setWhat('');
    setTitle('');
    setError(null);
    setAdded(null);
    setOpen(true);
  };

  const suggest = async () => {
    if (!what.trim()) {
      setError(ADD_SCENE_SUGGEST_NEEDS_WHAT);
      return;
    }
    setError(null);
    const ok = await ask('scene_title');
    if (!ok) return;
    setSuggesting(true);
    try {
      const next = await onSuggestTitle(what.trim());
      if (next) setTitle(next);
    } catch (err) {
      setError(err?.message || 'No title came back. Try again, or type one.');
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async () => {
    if (!what.trim()) {
      setError(ADD_SCENE_NEEDS_WHAT);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await onAdd({
        afterSceneId: after || undefined,
        whatHappens: what.trim(),
        title: title.trim(),
      });
      setOpen(false);
      if (draftKey) clearPlotTrackDraft(draftKey);
      setAdded(saved?.title || title.trim() || null);
    } catch (err) {
      setError(err?.message || 'Could not add that scene.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="pt-add-row pt-add-scene">
        <button
          type="button"
          className="pt-add-row__open"
          onClick={begin}
          aria-label={partName ? ADD_SCENE_PART(partName) : undefined}
        >
          {buttonLabel}
        </button>
        {added && (
          <p className="pt-add-scene__done" role="status">{ADD_SCENE_ADDED(added)}</p>
        )}
      </div>
    );
  }

  return (
    <form
      className="pt-add-row pt-add-row--open pt-add-scene--open"
      aria-label={ADD_SCENE}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {scenes.length > 0 && (
        <div className="pt-add-row__field pt-add-scene__field">
          <label htmlFor={`${idPrefix}-after`}>{ADD_SCENE_AFTER}</label>
          <select id={`${idPrefix}-after`} value={after} onChange={(event) => setAfter(event.target.value)} disabled={busy}>
            {groups.map((group) => (group.name ? (
              <optgroup key={group.key} label={group.name}>
                {group.scenes.map(sceneOption)}
              </optgroup>
            ) : (
              <React.Fragment key={group.key}>{group.scenes.map(sceneOption)}</React.Fragment>
            )))}
          </select>
        </div>
      )}
      <div className="pt-add-row__field pt-add-scene__field">
        <label htmlFor={`${idPrefix}-what`}>{ADD_SCENE_WHAT}</label>
        <textarea
          id={`${idPrefix}-what`}
          rows={3}
          value={what}
          onChange={(event) => setWhat(event.target.value)}
          disabled={busy}
          aria-describedby={`${idPrefix}-what-hint`}
        />
        <small id={`${idPrefix}-what-hint`}>{ADD_SCENE_WHAT_HINT}</small>
      </div>
      <div className="pt-add-row__field pt-add-scene__field">
        <label htmlFor={`${idPrefix}-title`}>{ADD_SCENE_TITLE}</label>
        <span className="pt-add-scene__title-row">
          <input
            id={`${idPrefix}-title`}
            ref={titleRef}
            type="text"
            value={title}
            autoComplete="off"
            onChange={(event) => {
              restoreTitleFocus.current = true;
              titleCaret.current = event.target.selectionStart;
              setTitle(event.target.value);
            }}
            disabled={busy || suggesting}
            maxLength={255}
            aria-describedby={`${idPrefix}-title-hint`}
          />
          <button type="button" onClick={suggest} disabled={busy || suggesting}>
            {suggesting ? ADD_SCENE_SUGGESTING : ADD_SCENE_SUGGEST}
          </button>
        </span>
        <small id={`${idPrefix}-title-hint`}>{ADD_SCENE_TITLE_HINT}</small>
      </div>
      {error && <p className="pt-add-row__error" role="alert">{error}</p>}
      <div className="pt-add-row__actions">
        <button type="submit" disabled={busy || suggesting}>
          {busy ? ADD_SCENE_SAVING : ADD_SCENE_SAVE}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy}>
          {ADD_SCENE_CANCEL}
        </button>
      </div>
      {dialog}
    </form>
  );
}
